import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, of, takeUntil, timeout } from 'rxjs';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { environment } from '../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../../../shared/services/toast.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
@Injectable({
  providedIn: 'root'
})
export class LogsPaginationService {
  private isInitialLoaded = false;

  private OrderBy: string = ''
  private payload: any | null = null;
  private reportKey!: string;

  private currentPage = 1;
  private limit = 10;          // default limit
  private offset = 0;          // default offset
  private totalCount = 0;
  private fullListData: any[] = [];

  showTable$ = new BehaviorSubject<boolean>(false);
  totalCount$ = new BehaviorSubject<number>(0);
  payloadChanged$ = new BehaviorSubject<boolean>(false);
  private mapByUrlCache = new Map<string, any>();
  sitename: string;

  constructor(
    private api: ApiCallsService,
    private aggrid: AgGridTableService,
    private http: HttpClient,
    private toast: ToastService
  ) { }
  downloadheader: string = ''
  /** Initial load */
  initFilters(
    payload: any,
    reportKey: string,
    destroy$: any,
    OrderBY: string = '',
    downloadheader: string = ''
  ) {
    const loginType = localStorage.getItem('login_Type');
    const normalizedPayload = { ...payload };

    if (loginType === 'CO') {
      const coLocationId = localStorage.getItem('login_id') || '';
      if (!normalizedPayload.loc_id) {
        normalizedPayload.loc_id = coLocationId;
      }
      if (!normalizedPayload.site_ids) {
        const consumerSiteId = sessionStorage.getItem('consumerSiteId');
        if (consumerSiteId) {
          normalizedPayload.site_ids = consumerSiteId;
        }
      }
    }

    const payloadChanged =
      this.isPayloadChanged(this.payload, normalizedPayload) ||
      this.OrderBy !== OrderBY;

    this.payloadChanged$.next(payloadChanged);

    if (payloadChanged) {
      this.reset();
    }
    this.downloadheader = downloadheader;
    this.OrderBy = OrderBY;
    this.payload = { ...normalizedPayload };
    this.reportKey = reportKey;
    this.showTable$.next(true);
    this.fetchPage(destroy$);
  }


  /** Pagination handler (component stays unchanged) */
  changePage(
    event: { page: number; pageSize: number },
    destroy$: any
  ) {
    this.currentPage = event.page;
    this.limit = event.pageSize;

    // ✅ correct offset calculation
    this.offset = (this.currentPage - 1) * this.limit;

    if (this.offset >= this.totalCount && this.totalCount > 0) return;

    this.fetchPage(destroy$);
  }

  /** Reset */
  reset() {
    this.showTable$.next(false);
    this.totalCount$.next(0);

    this.fullListData = []
    this.currentPage = 1;
    this.offset = 0;
    this.totalCount = 0;

    this.aggrid.processTableData([]);
  }

  // ---------------- PRIVATE ---------------- //

  private fetchPage(destroy$: any) {
    console.log(this.payload);

    this.api.getLogs(
      this.payload.from_date,
      this.payload.to_date,
      this.reportKey,
      this.offset,       // 👈 new
      this.limit,        // 👈 new
      this.payload.site_ids || this.payload.site_id,
      this.payload.appliance,
      this.payload.loc_id,
      this.payload.selected_date,
      this.payload.search,
      this.payload.status,
      this.OrderBy,
      this.payload.statusSelect,
      this.payload.type,
      this.payload.loc_idFrom,
      this.payload.loc_idTo,
      this.payload,
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.processResponse(data),
        error: (err) => {
          console.error(err);
          this.toast.showToast(err.error.Message, 'colored', 5000, 'danger')
          this.aggrid.processTableData([]);
        }
      });
  }

  private processResponse(data: any) {
    this.isInitialLoaded = true; // ✅ mark success
    console.log(data);

    const list = data.Data.list || [];
    this.totalCount = Number(data?.Data?.total ?? data?.Data?.count ?? data?.Data?.totalCount ?? 0);
    this.totalCount$.next(this.totalCount);

    // Ensure full list size for AgGrid virtual pagination
    while (this.fullListData.length < this.totalCount) {
      this.fullListData.push({
        id: `dummy-${this.fullListData.length}`,
        isDummy: true
      });
    }

    // Replace slice using offset
    list.forEach((item: any, i: number) => {
      const index = this.offset + i;
      if (index < this.totalCount) {
        this.fullListData[index] = { ...item, isDummy: false };
      }
    });

    this.aggrid.processTableData({
      ...data,
      Data: {
        ...data.Data,
        list: [...this.fullListData]
      }
    });
  }

  private isPayloadChanged(
    oldPayload: any,
    newPayload: any
  ): boolean {
    if (!oldPayload) return true;

    const keys = new Set([
      ...Object.keys(oldPayload),
      ...Object.keys(newPayload)
    ]);

    for (const key of keys) {
      if (oldPayload[key] !== newPayload[key]) {
        return true;
      }
    }

    return false;
  }

  downloadCSV(destroy$: any, showtrue: any[] = []) {
    if (!this.payload || !this.reportKey) {
      console.warn('CSV download aborted: payload not initialized');
      return;
    }

    const limitForCSV = this.totalCount > 0 ? this.totalCount : 10000;

    this.api.getLogs(
      this.payload.from_date,
      this.payload.to_date,
      this.reportKey,
      0,                 // 🔥 ALWAYS start from 0
      limitForCSV,       // 🔥 fetch all
      this.payload.site_ids || this.payload.site_id,
      this.payload.appliance,
      this.payload.loc_id,
      this.payload.selected_date,
      this.payload.search,
      this.payload.status,
      this.OrderBy,
      this.payload.statusSelect,
      this.payload.type,
      this.payload.loc_idFrom,
      this.payload.loc_idTo,
      this.payload,
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handleCSVResponse(data, showtrue),
        error: (err) => {
          console.error('❌ CSV download failed:', err)
          this.toast.showToast('CSV download failed: ' + err.error.Message, 'colored', 5000, 'danger')
        }

      });
  }

  private filterHeadersByVisibility(headers: any[], showtrue: any[]) {
    if (!Array.isArray(showtrue) || !showtrue.length) return headers;

    const visibleFields = new Set(
      showtrue
        .filter(col => col.hide === false)
        .map(col => col.field)
    );

    return headers.filter((h: any) =>
      visibleFields.has(h.key) // h.key must match field
    );
  }
  private filterRowDataByHeaders(list: any[], headers: any[]) {
    const allowedKeys = new Set(headers.map((h: any) => h.key));

    return list.map(row => {
      const filtered: any = {};
      for (const key of allowedKeys) {
        filtered[key] = row[key];
      }
      return filtered;
    });
  }


  async applyHeaderUrlMappingsForExport(
    headers: any[],
    rows: any[],
  ): Promise<any[]> {
    if (!headers?.length || !rows?.length) {
      return rows;
    }

    const urlHeaders = headers.filter(h =>
      h?.key && h?.MapByUrl && h?.MapByKey && h?.MapByValue
    );

    if (!urlHeaders.length) {
      return rows;
    }

    let updatedRows = [...rows];
    const encodedSite = localStorage.getItem('site_idLogin');
    const rawSite = this.payload?.site_ids || this.payload?.site_id || rows[0]?.site_id || rows[0]?.siteid || (encodedSite ? atob(encodedSite) : '');
    const siteIds: string[] = String(rawSite || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);

    for (const header of urlHeaders) {
      const cleanUrl = header.MapByUrl?.replace(/^\/+/, '') || '';
      const payloadKeyStr = header.MapByPayload ? JSON.stringify(header.MapByPayload) : '';
      const cacheKey = `${cleanUrl}_${siteIds.sort().join('_')}_${payloadKeyStr}`;
      let lookup: Record<string, any> = {};

      try {
        // ---------- CACHE ----------
        if (this.mapByUrlCache.has(cacheKey)) {
          lookup = this.mapByUrlCache.get(cacheKey);
        }
        // ---------- API ----------
        else {
          let extraParams: any = {};
          if (header.MapByPayload && typeof header.MapByPayload === 'object') {
            extraParams = { ...header.MapByPayload };
          }

          const mapByKey = typeof header.MapByKey === 'string' ? header.MapByKey : 'id';
          const mapByValue = header.MapByValue || 'name';

          const sitesToCall = siteIds.length > 0 ? siteIds : [''];
          const responses = await Promise.all(
            sitesToCall.map(siteId => {
              const params: any = { Limit: 10000, Offset: 0, ...extraParams };
              if (siteId) {
                params.site_id = siteId;
              }
              return firstValueFrom(
                this.http
                  .get<any>(`${environment.apiURL}/${cleanUrl}`, { params })
                  .pipe(
                    timeout(8000),
                    catchError(() => of(null))
                  )
              );
            })
          );

          lookup = responses.reduce((acc: any, res: any) => {
            const list = res?.Data?.list ?? res?.Data ?? res ?? [];
            if (!Array.isArray(list)) return acc;

            list.forEach((item: any) => {
              const key = item[mapByKey];
              const value = item[mapByValue];
              if (key != null && value != null) {
                acc[key] = value;
                acc[String(key)] = value;
              }
            });
            return acc;
          }, {});

          this.mapByUrlCache.set(cacheKey, lookup);
        }

        // ---------- APPLY ----------
        updatedRows = updatedRows.map(row => {
          const val = row[header.key];
          if (
            val !== undefined &&
            val !== null &&
            val !== '' &&
            val !== '---' &&
            val !== 'N/A'
          ) {
            const mapped = lookup[val] ?? lookup[String(val)];
            if (mapped !== null && mapped !== undefined) {
              return {
                ...row,
                [header.key]: mapped
              };
            }
          }
          return {
            ...row,
            [header.key]: 'N/A'
          };
        });

      } catch (err) {
        console.error(`Failed to map ${header.key} from URL ${cleanUrl}:`, err);
        updatedRows = updatedRows.map(row => ({
          ...row,
          [header.key]: 'N/A'
        }));
        continue;
      }
    }

    return updatedRows;
  }


  listSitenameMapping(list: any, showtrue: any) {
    const siteNameHeader = showtrue.find(
      (h: any) => h.field === 'site_name' && h.hide === false
    );
    const sitecodeNameHeader = showtrue.find(
      (h: any) => h.field === 'short_code' && h.hide === false
    );

    if (siteNameHeader) {
      const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
      if (Array.isArray(siteList) && siteList.length) {
        const siteMap = new Map(
          siteList.map((s: any) => [String(s.site_id), s.site_name])
        );

        list = list.map((row: any) => {
          if (!row.site_name && row.site_id) {
            return {
              ...row,
              site_name: siteMap.get(String(row.site_id)) || ''
            };
          }
          return row;
        });
      }
    }
    if (sitecodeNameHeader) {
      const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');
      if (Array.isArray(siteList) && siteList.length) {
        const siteMap = new Map(
          siteList.map((s: any) => [String(s.site_id), s.short_code])
        );

        list = list.map((row: any) => {
          if (!row.short_code && row.site_id) {
            return {
              ...row,
              short_code: siteMap.get(String(row.site_id)) || ''
            };
          }
          return row;
        });
      }
    }
    return list
  }

  private async handleCSVResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') return;

    let headers = data?.Data?.header || [];
    let list = data?.Data?.list || [];

    if (!headers.length || !list.length) return;

    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;

    list = await this.applyHeaderUrlMappingsForExport(headers, list);

    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.listSitenameMapping(list, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) return;

    // 🔥 Append total row AFTER filtering, using visible headers
    list = this.appendTotalRow(headers, list, 'csv');

    const csv = this.convertToCSVUsingHeader(headers, list);
    this.setSiteNameFromStorage();
    const fileName = `${this.downloadheader}-${this.sitename}.csv`;
    this.triggerCSVDownload(csv, fileName);
  }

  private convertToCSVUsingHeader(
    headers: Array<{ name: string; key: string; }>
    , data: any[]
  ): string {

    // 🔥 Filter out siteid column from CSV output
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid'
    );


    /* --------------------------------------------------
       🔹 Build site lookup map from localStorage
       -------------------------------------------------- */
    const siteList = JSON.parse(localStorage.getItem('siteList') || '[]');

    const siteMap = new Map<string, any>();
    siteList.forEach((s: any) => {
      if (s.site_id) {
        siteMap.set(String(s.site_id), s);
      }
    });

    // CSV header row
    const headerRow = validHeaders
      .map(h => `"${h.name}"`)
      .join(',');

    // CSV data rows
    const rows = data.map(row =>
      validHeaders
        .map(h => {
          let value = row[h.key];

          /* --------------------------------------------
             🔹 Fallback mapping for site_name / short_code
             -------------------------------------------- */
          if (
            (value === undefined || value === null || value === '') &&
            row.siteid &&
            (h.key === 'site_name' || h.key === 'short_code')
          ) {
            const site = siteMap.get(String(row.siteid));
            value = site?.[h.key] ?? '';
          }

          if (!row._isTotal && (value === undefined || value === null || value === '' || value === '---')) {
            value = 'N/A';
          }

          return `"${String(value ?? '').replace(/"/g, '""')}"`;
        })
        .join(',')
    );

    return [headerRow, ...rows].join('\n');
  }

  private triggerCSVDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.click();

    URL.revokeObjectURL(url);
  }
  private appendTotalRow(headers: any[], list: any[], target: 'pdf' | 'csv' | 'table' = 'pdf'): any[] {
    const isTargetTotalCol = (h: any) => {
      if (target === 'pdf') {
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true' || h.export_total === true || h.exportTotal === true) {
          return true;
        }
        if (h.pdf_total === false || h.pdf_total === 'false' || h.pdfTotal === false || h.pdfTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      } else if (target === 'csv') {
        if (h.csv_total === true || h.csv_total === 'true' || h.csvTotal === true || h.csvTotal === 'true' || h.export_total === true || h.exportTotal === true || h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return true;
        }
        if (h.csv_total === false || h.csv_total === 'false' || h.csvTotal === false || h.csvTotal === 'false' || h.export_total === false || h.exportTotal === false) {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      } else {
        if (h.table_total === true || h.table_total === 'true' || h.tableTotal === true || h.tableTotal === 'true' || h.grid_total === true || h.grid_total === 'true' || h.gridTotal === true || h.gridTotal === 'true') {
          return true;
        }
        if (h.table_total === false || h.table_total === 'false' || h.tableTotal === false || h.tableTotal === 'false' || h.grid_total === false || h.grid_total === 'false' || h.gridTotal === false || h.gridTotal === 'false') {
          return false;
        }
        if (h.pdf_total === true || h.pdf_total === 'true' || h.pdfTotal === true || h.pdfTotal === 'true') {
          return false;
        }
        return h.total === true || h.total === 'true' || h.grand_total === true || h.grandTotal === true;
      }
    };

    const totalHeaders = headers.filter(isTargetTotalCol);
    if (!totalHeaders.length) return list;

    const totalRow: any = {};
    totalRow['_isTotal'] = true;

    headers.forEach((h, index) => {
      if (isTargetTotalCol(h)) {
        const sum = list.reduce((acc, row) => {
          const val = parseFloat(row[h.key]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
        totalRow[h.key] = parseFloat(sum.toFixed(2));
      } else {
        totalRow[h.key] = index === 0 ? 'Total' : '';
      }
    });

    return [...list, totalRow];
  }
  downloadPDF(destroy$: any, showtrue: any[] = []) {
    if (!this.payload || !this.reportKey) {
      console.warn('PDF download aborted: payload not initialized');
      return;
    }

    const pageSizeForPDF = this.totalCount > 0 ? this.totalCount : 10000;

    this.api.getLogs(
      this.payload.from_date,
      this.payload.to_date,
      this.reportKey,
      0,                 // 🔥 ALWAYS start from 0
      pageSizeForPDF,       // 🔥 fetch all
      this.payload.site_ids || this.payload.site_id,
      this.payload.appliance,
      this.payload.loc_id,
      this.payload.selected_date,
      this.payload.search,
      this.payload.status,
      this.OrderBy,
      this.payload.statusSelect,
      this.payload.type,
      this.payload.loc_idFrom,
      this.payload.loc_idTo,
      this.payload,
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => this.handlePDFResponse(data, showtrue),
        error: (err) => {
          console.error('❌ PDF download failed:', err);
          this.toast.showToast('PDF download failed: ' + err.error.Message, 'colored', 5000, 'danger');
        }
      });
  }

  private async handlePDFResponse(data: any, showtrue: any[] = []) {
    if (data?.Status !== 'Success') return;

    let headers = data?.Data?.header || [];
    let list = data?.Data?.list || [];

    if (!headers.length || !list.length) return;

    list = this.aggrid.applyHeaderKeyMappings(headers, list) || [];
    if (!list.length) return;

    list = await this.applyHeaderUrlMappingsForExport(headers, list);

    headers = this.filterHeadersByVisibility(headers, showtrue);
    list = this.listSitenameMapping(list, showtrue);
    list = this.filterRowDataByHeaders(list, headers);

    if (!headers.length || !list.length) return;

    // 🔥 Append total row AFTER filtering, using visible headers
    list = this.appendTotalRow(headers, list, 'pdf');

    this.setSiteNameFromStorage();
    this.generatePDF(headers, list);
  }
  private generatePDF(
    headers: Array<{ name: string; key: string }>,
    data: any[]
  ) {
    const validHeaders = headers.filter(h =>
      h.key?.toLowerCase() !== 'siteid');

    // Decide page size based on column count
    let pageFormat: any = 'a4';

    const columnCount = validHeaders.length;

    if (columnCount <= 10) {
      pageFormat = 'a4';
    } else if (columnCount <= 16) {
      pageFormat = 'a3';
    } else if (columnCount <= 26) {
      pageFormat = 'a2';
    } else if (columnCount <= 32) {
      pageFormat = 'a1'
    } else {
      pageFormat = 'a0'
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: pageFormat
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 20; // left + right margin (10 + 10)
    const equalColumnWidth = usableWidth / columnCount;
    const columnStyles: any = {};

    validHeaders.forEach((_, index) => {
      columnStyles[index] = { cellWidth: equalColumnWidth };
    });

    // 🔥 Dynamic Font Calculation

    // Reference:
    // 12mm column ≈ font size 7 (minimum readable)
    // 22mm column ≈ font size 10
    // 35mm+ column ≈ font size 14–15

    let dynamicFontSize = Math.round(equalColumnWidth * 0.45);

    // Page-aware clamps (important)
    const fontLimits: any = {
      a4: { min: 6, max: 11 },
      a3: { min: 7, max: 13 },
      a2: { min: 8, max: 15 },
      a1: { min: 9, max: 18 }
    };

    const limits = fontLimits[String(pageFormat).toLowerCase()] || fontLimits.a4;

    dynamicFontSize = Math.max(
      limits.min,
      Math.min(dynamicFontSize, limits.max)
    );
    /* =====================================================
       ✅ HEADER SECTION
    ===================================================== */

    const payload = this.payload || {};

    const BLACKLIST = ['appliance', 'sensor_id', 'status', 'state', 'id', 'offset', 'limit', 'site_id', 'site_ids', 'site', 'config_id', 'tower_id', 'loc_id', 'loc_from', 'loc_to', 'loc_idfrom', 'loc_idto', 'from_location_id', 'to_location_id', 'location_id', 'other_type', 'alarm_id', 'water_loc_id', 'otherType', 'orderBy', 'per_page', 'page', 'fromdate', 'todate'];

    const runDate = new Date().toLocaleString();

    // ----- LEFT SIDE -----
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    doc.text(`Site : ${this.sitename || '-'}`, 14, 10);

    // ----- CENTER TITLE -----
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(
      this.downloadheader,
      pageWidth / 2,
      12,
      { align: 'center' }
    );

    // ----- RIGHT SIDE -----
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    doc.text(`Page 1 of 1`, pageWidth - 14, 10, { align: 'right' });
    doc.text(`Run Date : ${runDate}`, pageWidth - 14, 16, { align: 'right' });

    /* =====================================================
       ✅ FILTER ROW (From / To etc)
    ===================================================== */

    let filterY = 24;
    doc.setFontSize(9);

    Object.entries(payload).forEach(([key, value]) => {
      if (BLACKLIST.includes(key.toLowerCase())) return;
      if (!value) return;

      const lowerKey = key.toLowerCase();
      if ((lowerKey === 'selected_date' || lowerKey === 'selecteddate') && ((payload as any)['date'] || (payload as any)['Date'])) {
        return;
      }

      const label = this.formatKey(key);

      doc.text(`${label} : ${value}`, 14, filterY);
      filterY += 6;
    });

    // Divider line
    doc.line(10, filterY, pageWidth - 10, filterY);

    /* =====================================================
       ✅ TABLE
    ===================================================== */

    const tableHeaders = validHeaders.map(h => h.name);

    const tableRows = data.map(row =>
      validHeaders.map(h => {
        if (row._isTotal) {
          return row[h.key] ?? '';
        }
        const val = row[h.key];
        return (val === undefined || val === null || val === '' || val === '---') ? 'N/A' : val;
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableRows,
      startY: filterY + 5,
      styles: {
        fontSize: dynamicFontSize,
        cellPadding: 2,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 0,
        lineWidth: 0.2,
        fontSize: dynamicFontSize + 1
      },
      columnStyles, // ✅ equal width columns

      theme: 'grid',
      margin: { left: 10, right: 10 }
    });


    doc.save(`${this.downloadheader}-${this.sitename}.pdf`);
  }
  private formatKey(key: string): string {
    const customLabels: Record<string, string> = {
      unit_from: 'From Unit',
      unit_to: 'To Unit',
      from_unit: 'From Unit',
      to_unit: 'To Unit',
      unit: 'Unit',
      search: 'Search By',
      search_by: 'Search By',
      searchby: 'Search By',
      filter_search: 'Search By',
      filtersearch: 'Search By',
      search_text: 'Search By',
      searchtext: 'Search By',
      search_term: 'Search By',
      searchterm: 'Search By',
      search_val: 'Search By',
      searchval: 'Search By',
      search_value: 'Search By',
      searchvalue: 'Search By',
      search_query: 'Search By',
      searchquery: 'Search By',
      search_input: 'Search By',
      searchinput: 'Search By'
    };
    const lower = key.toLowerCase();
    if (customLabels[lower]) {
      return customLabels[lower];
    }
    if (lower === 'search' || lower.startsWith('search') || lower.endsWith('search') || lower.includes('_search') || lower.includes('search_')) {
      return 'Search By';
    }
    return key
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  private setSiteNameFromStorage(): void {

    let siteIdsRaw: string | null = this.payload?.site_ids || null;
    const siteListRaw = localStorage.getItem('siteList');
    if (!siteListRaw) {
      this.sitename = '';
      return;
    }

    try {
      const siteList = JSON.parse(siteListRaw);

      // ✅ fallback if empty
      if (!siteIdsRaw) {
        const encoded = localStorage.getItem('site_idLogin');
        siteIdsRaw = encoded ? atob(encoded) : null;
      }

      if (!siteIdsRaw) {
        this.sitename = '';
        return;
      }

      // ✅ normalize ids
      const siteIds = String(siteIdsRaw)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

      // ✅ find matches
      let matchedSites = siteList.filter((site: any) =>
        siteIds.includes(String(site.site_id))
      );

      // ✅ fallback if no match found
      if (matchedSites.length === 0) {
        const encoded = localStorage.getItem('site_idLogin');
        const decoded = encoded ? atob(encoded) : null;

        if (decoded) {
          matchedSites = siteList.filter((site: any) =>
            String(site.site_id) === decoded
          );
        }
      }

      const matchedNames = matchedSites.map((site: any) => site.site_name);

      // ✅ final display
      this.sitename =
        matchedNames.length > 3
          ? `${matchedNames.slice(0, 3).join(', ')} +${matchedNames.length - 3} more`
          : matchedNames.join(', ');

    } catch (e) {
      console.error('Invalid siteList in localStorage');
      this.sitename = '';
    }
  }
}
